import { useEffect } from "react";

/**
 * Hook to handle service worker updates, especially for iOS PWAs.
 *
 * iOS PWAs don't automatically check for SW updates like desktop browsers.
 * This hook:
 * 1. Checks for updates when the app becomes visible (user returns to app)
 * 2. Uses pageshow/focus events which are more reliable on iOS
 * 3. Checks periodically while the app is open (every 1 minute)
 * 4. Auto-reloads when an update is activated (works with skipWaiting: true)
 */
export function useServiceWorkerUpdate() {
  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    let registration: ServiceWorkerRegistration | null = null;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    // Check for updates
    const checkForUpdates = async () => {
      if (registration) {
        try {
          await registration.update();
        } catch {
          // Silently fail - user might be offline
        }
      }
    };

    // Handle visibility change (user returns to app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdates();
      }
    };

    // Handle pageshow - more reliable on iOS than visibilitychange
    const handlePageShow = () => {
      checkForUpdates();
    };

    // Handle focus - another backup trigger for iOS
    const handleFocus = () => {
      checkForUpdates();
    };

    // Handle when a new SW takes control
    const handleControllerChange = () => {
      // New service worker has taken control, reload to get fresh content
      window.location.reload();
    };

    // Set up listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("focus", handleFocus);
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      handleControllerChange,
    );

    // Get current registration and set up update detection
    navigator.serviceWorker.ready.then((reg) => {
      registration = reg;

      // Check for updates immediately on mount
      checkForUpdates();

      // Check every 1 minute for better iOS experience
      intervalId = setInterval(checkForUpdates, 60 * 1000);
    });

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("focus", handleFocus);
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        handleControllerChange,
      );
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);
}
